/**
 * SplitPanel — canonical 380px fixed left + flex-1 right layout.
 * Used inside HubShell's body slot for list+detail tabs.
 * Fills parent height; parent must provide a bounded flex context.
 *
 * Mobile: Shows either list or detail with back button.
 */

import React, { useState, useEffect } from 'react';
import { colors } from './colors';

interface SplitPanelProps {
  left: React.ReactNode;
  right: React.ReactNode;
  leftWidth?: number; // defaults to 380 per canonical spec
  selectedItem?: boolean; // true when an item is selected (triggers mobile detail view)
  onBack?: () => void; // callback when back button is pressed on mobile
}

const SplitPanel: React.FC<SplitPanelProps> = ({ left, right, leftWidth = 380, selectedItem, onBack }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Mobile layout — show list or detail
  if (isMobile) {
    const showDetail = selectedItem;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {showDetail && onBack && (
          <div style={{
            background: colors.navy2,
            borderBottom: `1px solid ${colors.navy3}`,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <button
              onClick={onBack}
              style={{
                background: 'none',
                border: `1px solid ${colors.gold}`,
                color: colors.gold,
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← Back to List
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', background: showDetail ? colors.offBg : colors.navy2 }}>
          {showDetail ? right : left}
        </div>
      </div>
    );
  }

  // Desktop layout — side by side
  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Left Panel */}
      <div
        style={{
          width: `${leftWidth}px`,
          minWidth: `${leftWidth}px`,
          background: colors.navy2,
          borderRight: `1px solid ${colors.navy3}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {left}
      </div>

      {/* Right Panel */}
      <div
        style={{
          flex: 1,
          background: colors.offBg,
          overflowY: 'auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {right}
      </div>
    </div>
  );
};

export default SplitPanel;
